import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { UserPlus } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useUnmappedPins, useBiometricEmployees, useEmployeeActions } from '../hooks/useBiometricAdmin';
import { relativeTime } from '../biometricTypes';

/** PINs that are punching on a device but belong to no Zynx employee yet. */
export function UnmappedPinsPanel() {
  const { data: unmapped, isLoading } = useUnmappedPins();
  const { data: employees } = useBiometricEmployees();
  const { mapUnmapped } = useEmployeeActions();
  const [choice, setChoice] = useState<Record<string, string>>({});

  const unassigned = (employees ?? []).filter(e => !e.biometric_id);

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (!unmapped?.length) {
    return (
      <Card className="p-6 text-center text-sm text-on-surface-variant">
        Every PIN that has punched is mapped to an employee.
      </Card>
    );
  }

  const map = async (pin: string) => {
    const userId = choice[pin];
    if (!userId) {
      toast.error('Pick an employee first.');
      return;
    }
    try {
      await mapUnmapped.mutateAsync({ pin, userId });
      toast.success(`PIN ${pin} mapped — past punches were reassigned too.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <Card className="space-y-3 p-4">
      <div>
        <h3 className="font-semibold text-on-surface">
          Unmapped PINs <Badge tone="warning">{unmapped.length}</Badge>
        </h3>
        <p className="text-sm text-on-surface-variant">
          These enroll numbers are punching but have no employee. Mapping one also backfills its history.
        </p>
      </div>

      <div className="space-y-2">
        {unmapped.map(item => (
          <div
            key={item.biometric_id}
            className="flex flex-wrap items-center gap-3 rounded-md border border-outline-variant p-3"
          >
            <div className="min-w-[140px]">
              <div className="font-mono font-semibold text-on-surface">PIN {item.biometric_id}</div>
              <div className="text-xs text-on-surface-variant">
                {item.punch_count} punches · last {relativeTime(item.last_punch_at)}
                {item.device_name ? ` · device name "${item.device_name}"` : ''}
              </div>
            </div>
            <select
              className="h-9 flex-1 rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
              value={choice[item.biometric_id] ?? ''}
              onChange={e => setChoice(prev => ({ ...prev, [item.biometric_id]: e.target.value }))}
            >
              <option value="">Select employee…</option>
              {unassigned.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.display_name}
                  {emp.department ? ` — ${emp.department}` : ''}
                </option>
              ))}
            </select>
            <Button size="sm" onClick={() => map(item.biometric_id)}>
              <UserPlus className="mr-1 h-3.5 w-3.5" />
              Map
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
